import { styled } from '@mui/material/styles';

import { mergeClasses } from 'minimal-shared/utils';

import { flagIconClasses } from './classes';

export type FlagIconProps = React.ComponentProps<typeof FlagRoot> & {
    code?: string;
};

const FlagRoot = styled('span')(({ theme }) => ({
    width: 26,
    height: 20,
    flexShrink: 0,
    overflow: 'hidden',
    borderRadius: '5px',
    alignItems: 'center',
    display: 'inline-flex',
    justifyContent: 'center',
    backgroundColor: theme.vars.palette.background.neutral,
}));

const FlagImg = styled('img')(() => ({
    width: '100%',
    height: '100%',
    maxWidth: 'unset',
    objectFit: 'cover',
}));

/**
 * Renders a country flag icon based on the provided country code.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.code - The ISO 3166-1 alpha-2 country code (e.g., 'US', 'GB').
 * @param {string} [props.className] - Additional CSS class name for the root element.
 * @param {SxProps} [props.sx] - The system prop that allows defining system overrides as well as additional CSS styles.
 * @param {...Object} props.other - Any other props will be spread to the root element.
 * @returns {React.ReactElement|null} The flag icon component or null if no code is provided.
 *
 * @example
 * // Basic usage
 * <FlagIcon code="US" />
 *
 * // With custom styling
 * <FlagIcon code="GB" className="custom-class" sx={{ width: 32 }} />
 */
export function FlagIcon({ code, className, sx, ...other }: FlagIconProps) {
    if (!code) {
        return null;
    }

    return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <FlagRoot className={mergeClasses([flagIconClasses.root, className])} sx={sx} {...other}>
            <FlagImg
                loading="lazy"
                alt={code}
                src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${code?.toUpperCase()}.svg`}
                className={flagIconClasses.img}
            />
        </FlagRoot>
    );
}
